import { Template } from 'lib-shared-legacy-types';
import { connect } from 'react-redux';
import { arrayPush } from 'redux-form';

import { CreateAttachmentApiArg, filesApi } from 'apis/files';
import { DEFAULT_LANG_CODE } from 'app/utils/supportedLanguages';
import { AppDispatch } from 'bootstrap.store';
import { withTemplates } from 'hocs';
import { TemplatesQueryParams } from 'hocs/templates/api/types';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { getCurrentConversationLanguage } from '../../../../conversation/selectors';
import * as personalActions from '../../../../settings/actions/personal';
import { hasFeature } from '../../../featureFlag';
import RichTextField, {
  NormalizedTemplate,
  PropsT as RichTextFieldProps,
} from '../components/RichTextField';

const { personalSubmitMessageWithEnter } = personalActions.actions;

type StateProps = Pick<
  RichTextFieldProps,
  | 'language'
  | 'submitMessageWithEnter'
  | 'hasFFWriteHTMLEmail'
  | 'hasFFRecommendedQuickReplies'
  | 'hasLanguageTranslationControlFF'
>;

type DispatchProps = Pick<
  RichTextFieldProps,
  'arrayPush' | 'personalSubmitMessageWithEnter' | 'uploadAttachment'
>;

type OwnProps = Omit<
  RichTextFieldProps,
  keyof DispatchProps | keyof StateProps
>;

const mapStateToProps = (state: any): StateProps => {
  return {
    submitMessageWithEnter: state.settings.personal.submitMessageWithEnter,
    hasFFWriteHTMLEmail: hasFeature(state, 'write-html-email'),
    hasFFRecommendedQuickReplies: hasFeature(
      state,
      'recommended-quick-replies',
    ),
    hasLanguageTranslationControlFF: hasFeature(
      state,
      'templates-language-translation-control',
    ),
    language: getCurrentConversationLanguage(state),
  };
};

const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    arrayPush: (form: string, field: string, value: any) =>
      dispatch(arrayPush(form, field, value)),
    personalSubmitMessageWithEnter: bindActionToPromise(
      dispatch,
      personalSubmitMessageWithEnter,
    ),
    uploadAttachment: (payload: CreateAttachmentApiArg) => {
      return dispatch(
        filesApi.endpoints.createAttachment.initiate(payload),
      ).unwrap();
    },
  };
};

const templateNormalizer = (
  template: Template,
  params: TemplatesQueryParams,
): NormalizedTemplate => {
  const locale = params.locale_translations || DEFAULT_LANG_CODE;
  const defaultSubject = template.subject ?? '';

  return {
    name: template.name,
    subject:
      locale === DEFAULT_LANG_CODE
        ? defaultSubject
        : template.translations?.[locale]?.subject || defaultSubject, // Fallback to template subject if there is no translation for subject
    content:
      locale === DEFAULT_LANG_CODE
        ? template.content
        : template.translations?.[locale]?.content || template.content, // Fallback to template content if there is no translation for content,
  };
};

const ConnectedRichTextField = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps,
  null,
  {
    forwardRef: true,
  },
)(RichTextField);

export default withTemplates<OwnProps, NormalizedTemplate>(
  ConnectedRichTextField as any,
  {
    infinite: true,
    itemNormalizer: templateNormalizer,
    initialParams: {
      type: 'quick_response',
      sort_by: 'name:asc',
    },
  },
);
